import { Signal, computed } from '@angular/core';
import { AccountRole, Account } from '@moxi.gmbh/moxi-typescriptmodels';
import { signalStoreFeature, type, withComputed } from '@ngrx/signals';
import { ApplicationState } from './application.store';
import {
  hasAnyRole,
  hasAnyAccountRole as _hasAnyAccountRole,
  hasAnyTeamRole
} from '@shared/utils';
import { accountRolesInfo, RoleInfo, teamRolesInfo } from '@shared/models';

interface StoreWithSelectedAccount {
  selectedAccount: () => Account | undefined;
}

// Export standalone computed signals that other features can import directly
export const createHasServiceComputedSignals = (
  store: StoreWithSelectedAccount
): {
  hasRideBookingService: Signal<boolean>;
  hasDispatcherService: Signal<boolean>;
  hasOperatorService: Signal<boolean>;
  hasIntensiveCareService: Signal<boolean>;
} => {
  // Services computation
  const hasRideBookingService = computed(
    () =>
      !!store
        .selectedAccount()
        ?.organisationTypes.find(
          (organisation: string) => organisation === 'medical'
        )
  );

  const hasDispatcherService = computed(
    () =>
      !!store
        .selectedAccount()
        ?.organisationTypes.find(
          (organisation: string) => organisation === 'fleet'
        )
  );

  const hasOperatorService = computed(
    () =>
      !!store
        .selectedAccount()
        ?.organisationTypes.find(
          (organisation: string) => organisation === 'operator'
        )
  );

  const hasIntensiveCareService = computed(
    () =>
      !!store
        .selectedAccount()
        ?.activeServices.find(service => service === 'intensive_care')
  );

  return {
    hasRideBookingService,
    hasDispatcherService,
    hasOperatorService,
    hasIntensiveCareService
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const withComputedAuthentication = () =>
  signalStoreFeature(
    type<{ state: ApplicationState }>(),
    withComputed(store => {
      const profile = computed(() => store.userProfile());
      const account = computed(() => store.selectedAccount());
      const adminRoles: AccountRole[] = ['admin', 'owner', 'operator'];

      const accountId = computed(() => {
        if (hasOnlyOneAccount()) {
          const uniqueAccountId = profile()?.accountPermissions[0]?.accountId;

          return account()?.accountId ?? uniqueAccountId;
        }

        return account()?.accountId;
      });

      const isLoggedIn = computed(() => !!profile()?.userId);

      const isSuperAdmin = computed(() => profile()?.userRole === 'superadmin');

      const isOperator = computed(() => hasAnyRole(profile(), ['operator']));

      // Create the shared services signals
      const hasServiceSignals = createHasServiceComputedSignals(store);
      const hasRideBookingService = hasServiceSignals.hasRideBookingService;
      const hasDispatcherService = hasServiceSignals.hasDispatcherService;
      const hasOperatorService = hasServiceSignals.hasOperatorService;
      const hasIntensiveCareService = hasServiceSignals.hasIntensiveCareService;

      const isAdmin = computed(
        () =>
          isSuperAdmin() ||
          isOperator() ||
          _hasAnyAccountRole(profile(), accountId(), adminRoles)
      );

      const isRideBookerWithoutTeam = computed(
        () =>
          isSuperAdmin() ||
          isOperator() ||
          _hasAnyAccountRole(profile(), accountId(), [
            ...adminRoles,
            'ridebooker'
          ])
      );

      const isRideBooker = computed(
        () =>
          (isRideBookerWithoutTeam() ||
            hasAnyTeamRole(profile(), accountId(), ['ridebooker']) ||
            hasAnyRole(profile(), ['ridebooker'])) &&
          hasRideBookingService()
      );

      const isDispatcherWithoutTeam = computed(
        () =>
          isSuperAdmin() ||
          isOperator() ||
          _hasAnyAccountRole(profile(), accountId(), [
            ...adminRoles,
            'dispatcher'
          ])
      );

      const isDispatcher = computed(
        () =>
          (isDispatcherWithoutTeam() ||
            hasAnyTeamRole(profile(), accountId(), ['dispatcher']) ||
            hasAnyRole(profile(), ['dispatcher'])) &&
          hasDispatcherService()
      );

      const hasCeliosIntegrationService = computed(
        () =>
          !!account()?.activeServices.find(
            service => service === 'celios_integration'
          )
      );

      const hasDispoliveIntegrationService = computed(
        () =>
          !!account()?.activeServices.find(
            service => service === 'dispolive_integration'
          )
      );

      const hasOnlyOneAccount = computed(
        () =>
          profile()?.accountPermissions?.length === 1 &&
          !isSuperAdmin() &&
          !isOperator()
      );

      const hasNoAccount = computed(
        () =>
          profile()?.accountPermissions?.length === 0 &&
          !isSuperAdmin() &&
          !isOperator()
      );

      const hasOnlyOperatorService = computed(
        () => account()?.organisationTypes?.length === 1 && hasOperatorService()
      );

      const getValidAccountRoles = computed(() => {
        const roleInfos = accountRolesInfo.filter(
          role => role.id === 'admin' || role.id === 'owner'
        );

        if (hasOperatorService()) {
          roleInfos.push(accountRolesInfo.find(role => role.id === 'operator'));
        }

        if (hasRideBookingService()) {
          roleInfos.push(
            accountRolesInfo.find(role => role.id === 'ridebooker')
          );
        }

        if (hasDispatcherService()) {
          roleInfos.push(
            accountRolesInfo.find(role => role.id === 'dispatcher')
          );
        }

        return roleInfos;
      });

      const getValidTeamRoles = computed(() => {
        const roleInfos: RoleInfo[] = [];

        if (hasRideBookingService()) {
          roleInfos.push(teamRolesInfo.find(role => role.id === 'ridebooker'));
        }

        if (hasDispatcherService()) {
          roleInfos.push(teamRolesInfo.find(role => role.id === 'dispatcher'));
        }

        return roleInfos;
      });

      return {
        // Authentication
        isLoggedIn,

        // Authorization
        isSuperAdmin,
        isOperator,
        isAdmin,
        isRideBookerWithoutTeam,
        isRideBooker,
        isDispatcherWithoutTeam,
        isDispatcher,
        hasRideBookingService,
        hasDispatcherService,
        hasCeliosIntegrationService,
        hasDispoliveIntegrationService,
        hasOperatorService,
        hasIntensiveCareService,
        hasOnlyOneAccount,
        hasNoAccount,
        hasOnlyOperatorService,
        getValidAccountRoles,
        getValidTeamRoles
      };
    })
  );
